import React, { Component } from "react";
import type { WithTranslation } from "react-i18next";
import { withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandshakeAlt } from "@fortawesome/pro-light-svg-icons";
import { faHeadset, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { Button, IconButton, MenuItem, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { cloneDeep } from "lodash";

import type { IPartner, IPartnerSlim, IRole, ISupportingPartner } from "App/Modules/Models";
import type { IPartnersRepository, IRolesRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import { jsonEqual } from "../../../Shared";

import "./_supporting-partners-dialog.scss";

type InjectedProps = {
    onClose: () => void;
    updatePartner: (up: IPartner) => void;
    partner: IPartner;
    partnersRepository: IPartnersRepository;
    rolesRepository: IRolesRepository;
};

type OwnProps = WithTranslation & InjectedProps;

type OwnState = {
    partners: IPartnerSlim[];
    roles: IRole[];
    supportingPartners: ISupportingPartner[];
    selectedPartner: IPartnerSlim;
    searchPartner: string;
};

class SupportingPartnersDialogInternal extends Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);
        this.state = {
            partners: null,
            roles: null,
            supportingPartners: props.partner.supportingPartners || [],
            selectedPartner: null,
            searchPartner: "",
        };
        if (props.partner) {
            this.saveLocalPartnerCopy(props.partner);
        }
    }
    componentDidMount() {
        this.getPartners();
        this.getPartnerRoles();
    }
    private localPartner?: IPartner;
    saveLocalPartnerCopy(partner: IPartner) {
        this.localPartner = cloneDeep(partner);
    }
    filterSupportingPartners = (partnersList: IPartnerSlim[]) => {
        const partnersToRemove: string[] = [];

        partnersToRemove.push(this.props.partner.id);

        this.state.supportingPartners.forEach((sp) => {
            partnersToRemove.push(sp.parentPartner.id);
        });

        return partnersList.filter((p) => !partnersToRemove.includes(p.id));
    };
    getPartners() {
        extendWithLoadingAndFail(this.props.partnersRepository.getPartnersIncludingGlobal()).subscribe(
            (response) => {
                if (response.data && !jsonEqual(this.state.partners, response.data)) {
                    this.setState({
                        partners: this.filterSupportingPartners(response.data),
                    });
                }
            },
            (error) => {
                console.error(error);
            },
        );
    }
    getPartnerRoles() {
        extendWithLoadingAndFail(this.props.rolesRepository.getPartnerRoles()).subscribe(
            (response) => {
                if (response.data && !jsonEqual(this.state.roles, response.data)) {
                    this.setState({ roles: response.data });
                }
            },
            (error) => {
                console.error(error);
            },
        );
    }
    handleClose() {
        this.props.onClose();
    }
    handleSelectPartner(e: React.ChangeEvent<any>, partner: IPartnerSlim) {
        this.setState({ selectedPartner: partner });
        const newSp = {
            name: "",
            parentPartnerId: partner.id,
            parentPartner: partner,
            childPartnerId: this.props.partner.id,
            roleId: "",
        } as ISupportingPartner;
        this.setState((state) => ({
            supportingPartners: [...state.supportingPartners, newSp],
            partners: state.partners.filter((p) => p.id !== partner.id),
            selectedPartner: null,
            searchPartner: "",
        }));
    }
    handleRemoveSupportingPartner(supportingPartner: ISupportingPartner) {
        this.setState((state) => {
            const partners = [...state.partners, supportingPartner.parentPartner];
            partners.sort((a: any, b: any) => (a.name > b.name ? 1 : -1));
            return {
                supportingPartners: state.supportingPartners.filter(
                    (sp) => sp.parentPartnerId !== supportingPartner.parentPartnerId,
                ),
                partners,
            };
        });
    }
    handleDescriptionChange(e: React.ChangeEvent<HTMLTextAreaElement>, sp: ISupportingPartner) {
        this.setState((state) => {
            const updatedSupportingPartners = [...state.supportingPartners];
            updatedSupportingPartners.find((usp) => usp.parentPartnerId === sp.parentPartnerId).name = e.target.value;
            return { supportingPartners: updatedSupportingPartners };
        });
    }
    handleSelectRoleChange(e: React.ChangeEvent<any>, sp: ISupportingPartner) {
        this.setState((state) => {
            const updatedSupportingPartners = [...state.supportingPartners];
            updatedSupportingPartners.find((usp) => usp.parentPartnerId === sp.parentPartnerId).roleId = e.target.value;
            return { supportingPartners: updatedSupportingPartners };
        });
    }
    handleSaveSupportingPartners() {
        const { supportingPartners } = this.state;
        const patchObject = {} as IPartner;
        if (!jsonEqual(this.localPartner.supportingPartners, supportingPartners)) {
            patchObject.supportingPartners = supportingPartners;
        }

        extendWithLoadingAndFail(this.props.partnersRepository.patch(this.props.partner.id, patchObject)).subscribe(
            (response) => {
                if (!response.hasError) {
                    this.props.updatePartner(response.data);
                    this.props.onClose();
                }
            },
            (error) => {
                // handle error some way
                console.error(error);
            },
        );
    }
    render() {
        const { partners, roles, supportingPartners, selectedPartner, searchPartner } = this.state;
        const { partner, t } = this.props;

        return (
            <div className="card dialog supporting-partners-dialog">
                <div className="dialog__content">
                    <div className="dialog__section dialog__section--full">
                        <h1 className="title title--h1 title--no-spacing">
                            {t("Gui.Manage.AddAnything.AddTitleFormat", {
                                typeToCreate: t("Entities.SupportingPartner.SupportingPartner").toLowerCase(),
                            })}
                        </h1>
                        <div className="state-tag state-tag--info-light">
                            <FontAwesomeIcon icon={faHandshakeAlt} fixedWidth className="state-tag__icon" />
                            {partner.name}
                        </div>
                    </div>
                    <div className="dialog__section dialog__section--small">
                        <h3 className="title title-h3">{t("Gui.Manage.SelectPartners")}</h3>
                        {partners ? (
                            <Autocomplete
                                options={partners}
                                getOptionLabel={(p) => p.name}
                                value={selectedPartner}
                                inputValue={searchPartner}
                                onChange={(e, value: IPartnerSlim) => this.handleSelectPartner(e, value)}
                                onInputChange={(e, value) =>
                                    this.setState({
                                        searchPartner: value,
                                    })
                                }
                                renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
                                blurOnSelect
                            />
                        ) : null}
                    </div>
                    <div className="dialog__section dialog__section--large dialog__section--flipped-spacing">
                        <div className="card card--nested card--alt">
                            <h3 className="title title-h3 supporting-partners__header">
                                <FontAwesomeIcon
                                    className="supporting-partners__header--icon"
                                    icon={faHeadset}
                                    fixedWidth
                                />
                                {t("Gui.Manage.ChosenPartners")}
                            </h3>
                            <ul className="entity-list">
                                {supportingPartners.length > 0 ? (
                                    supportingPartners.map((sp) => (
                                        <li
                                            className="entity-list__item entity-list__item--stuffing"
                                            key={sp.parentPartnerId}
                                        >
                                            <div className="entity-list__item-row">
                                                <div className="entity-list__item-value entity-list__item-value--flex-space-between">
                                                    <div className="state-tag state-tag--light">
                                                        <FontAwesomeIcon
                                                            className="state-tag__icon"
                                                            icon={faHandshakeAlt}
                                                            fixedWidth
                                                        />
                                                        {sp.parentPartner.name}
                                                    </div>
                                                    <IconButton
                                                        size="small"
                                                        className="icon-button"
                                                        onClick={() => this.handleRemoveSupportingPartner(sp)}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faTimes}
                                                            fixedWidth
                                                            className="icon-button__icon"
                                                        />
                                                    </IconButton>
                                                </div>
                                            </div>
                                            <div className="entity-list__item-panel">
                                                <div className="entity-list__item-value entity-list__item-value--extra-spacing">
                                                    <p className="caption">{t("Entities.Common.Description")}</p>
                                                    <textarea
                                                        className="supporting-partners-dialog__supporting-partner-description"
                                                        rows={3}
                                                        value={sp.name}
                                                        onChange={(e) => this.handleDescriptionChange(e, sp)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="entity-list__item-panel entity-list__item-panel--top-align">
                                                <div className="entity-list__item-value">
                                                    <p className="caption">{t("Gui.Manage.SelectRole")}</p>
                                                    {roles ? (
                                                        <TextField
                                                            className="supporting-partners-dialog__role-select"
                                                            size="small"
                                                            variant="outlined"
                                                            select
                                                            value={sp.roleId}
                                                            onChange={(e) => this.handleSelectRoleChange(e, sp)}
                                                        >
                                                            {roles.map((r) => (
                                                                <MenuItem key={r.id} value={r.id}>
                                                                    {r.name}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </li>
                                    ))
                                ) : (
                                    <li className="entity-list__item">
                                        <p className="title title--h3 title--muted">
                                            {t("Gui.Manage.WaitingForChosenPartners")}
                                        </p>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                    <div className="dialog__section dialog__section--buttons">
                        <Button color="default" onClick={() => this.handleClose()}>
                            {t("Gui.Buttons.Cancel")}
                        </Button>
                        <Button color="primary" variant="contained" onClick={() => this.handleSaveSupportingPartners()}>
                            {t("Gui.Manage.SaveSupportingPartners")}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export const SupportingPartnersDialog = withTranslation()(SupportingPartnersDialogInternal);
